import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProjectLabels } from "./policySlide";
import {
  fetchProjects,
  selectProjectById,
  selectProjectIds
} from "../projects/projectsSlice";
import styles from "./policy.module.scss";
import { Attendance } from "../attendance/Attendance";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { Navigate } from "react-router-dom";

export const Policy = () => {
  const dispatch = useDispatch();
  const allLabels = useSelector((state) => state.policy?.policies ?? null);
  const orderedProjectIds = useSelector(selectProjectIds);
  const projectStatus = useSelector(state => state.project?.status ?? "idle");
  const [loading, setLoading] = useState(true);
  const user = useSelector((state) => state.authentication?.user);
  const isAdminOrQA = user && (user.is_admin || user.is_admin_or_qa);

  useEffect(() => {
    const loadLabels = async () => {
      setLoading(true);
      try {
        await dispatch(getProjectLabels());
      } catch (error) {
        console.error('Error loading labels:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLabels();
  }, [dispatch]);

  useEffect(() => {
    if (projectStatus === "idle") {
      dispatch(fetchProjects());
    }
  }, [projectStatus, dispatch]);
  const projects = useSelector((state) => orderedProjectIds.map(id => selectProjectById(state, id)));

  return (
    <div className={styles.policyPageContainer}>
      <div className={styles.policyPageHeader}>
        <div className={styles.headerContent}>
          <h1 className={styles.pageTitle}>Policy Management</h1>
          <p className={styles.pageSubtitle}>
            Manage and organize your content moderation policies
          </p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.attendanceSection}>
            <Attendance />
          </div>
          {isAdminOrQA && (
            <div className={styles.actionButtons}>
              <Link to="/policy/create" className={styles.createButton}>
                <i className=""></i>
                Create New Policy
              </Link>
              <Link to="/category" className={styles.createButton}>
                <i className=""></i>
                Category List
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className={styles.policyPageContent}>
        <div className={styles.mainContentSection}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading policies...</p>
            </div>
          ) : allLabels && allLabels.length > 0 ? (
            <LabelCategoryTree allLabels={allLabels} isAdminOrQA={isAdminOrQA} projects={projects} />
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateIcon}>
                <i className="fas fa-file-alt"></i>
              </div>
              <h3>No Policies Found</h3>
              <p>Get started by creating your first policy</p>

              {isAdminOrQA && (
                <Link to="/policy/create" className={styles.emptyStateButton}>
                  Create Your First Policy
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export function LabelCategoryTree({ allLabels: allLabelsFromProps, isAdminOrQA, projects }) {
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState(0);
  
  // Store the original allLabels and use filtered version for display
  const allLabels = selectedProjectId 
    ? allLabelsFromProps.filter(l => String(l.project_id) === String(selectedProjectId))
    : allLabelsFromProps;
  
  // Set default selected label on mount
  useEffect(() => {
    if (!selectedLabel && allLabelsFromProps && allLabelsFromProps.length > 0) {
      const defaultLabel = allLabelsFromProps.find(l => l.project_id === 0);
      if (defaultLabel) {
        setSelectedLabel(defaultLabel);
      }
    }
  }, [allLabelsFromProps]);
  // Group labels by category_id with search filtering
  const grouped = useMemo(() => {
    const map = {};

    // Ensure allLabels is an array
    let labelsArray = [];
    if (Array.isArray(allLabels)) {
      labelsArray = allLabels;
    } else if (allLabels && typeof allLabels === 'object') {
      labelsArray = allLabels.data || allLabels.labels || allLabels.items || Object.values(allLabels);
      if (!Array.isArray(labelsArray)) {
        labelsArray = [];
      }
    }

    // Filter labels based on search term (only by name and category, not definition)
    const filteredLabels = searchTerm.trim() === '' ? labelsArray : labelsArray.filter((label) => {
      if (!label || typeof label !== 'object') return false;

      const labelName = (label.name || '').toLowerCase();
      const categoryName = ((label.label_category && label.label_category.name) ||
        label.category_name ||
        label.category || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      const nameMatch = labelName.includes(searchLower);
      const categoryMatch = categoryName.includes(searchLower);
      const shouldInclude = nameMatch || categoryMatch;

      return shouldInclude;
    });

    // Group filtered labels by category
    filteredLabels.forEach((label) => {
      const catName = (label.label_category && label.label_category.name) ||
        label.category_name ||
        label.category ||
        "Uncategorized";

      if (!map[catName]) map[catName] = [];
      map[catName].push(label);
    });

    return map;
  }, [allLabels, searchTerm, selectedProjectId]);

  return (
    <div className={styles.labelCategoryTree}>
      <div className={styles.categorySidebar}>
        <div className={styles.sidebarHeader}>
        <a 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            setSelectedProjectId(0);
            const poLabel = allLabelsFromProps.find(l => l.project_id === 0);
            if (poLabel) {
              setSelectedLabel(poLabel);
            }
          }}
          style={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }}
        >
          <h3 style={{ color: '#007bff' }}>Project overview</h3>
        </a>
          <hr/>
          <div className={styles.searchContainer}>
            <div className={styles.searchInputWrapper}>
              <select
                value={selectedProjectId}
                onChange={e => setSelectedProjectId(e.target.value)}
                className={styles.formSelect}
              >
                <option value="">Select Project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {selectedProjectId > 0 && (
          <div className={styles.sidebarHeader}>
            <h3>Policy Categories</h3>
            <div className={styles.searchContainer}>
              <div className={styles.searchInputWrapper}>
                <FontAwesomeIcon
                  icon={faSearch}
                  className={styles.searchIcon}
                />
                <input
                  type="text"
                  placeholder="Search policies..."
                  className={styles.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        <div className={styles.categoryList}>
          {selectedProjectId > 0 ? (
            Object.keys(grouped).length > 0 && (
              Object.entries(grouped).map(([categoryName, labels]) => (
                <div key={categoryName} className={styles.categoryGroup}>
                  <div className={styles.categoryHeader}>
                    <div className={styles.categoryInfo}>
                      <span className={styles.categoryName}>{categoryName}</span>
                      <span className={styles.categoryCount}>
                        {labels.length}
                      </span>
                    </div>
                  </div>
                  <div className={styles.labelList}>
                    {labels.map((label) => (
                      <div key={label.id} className={styles.labelItem}>
                        <div
                          className={`${styles.labelLink} ${selectedLabel?.id === label.id ? styles.selected : ''
                            }`}
                          onClick={() => setSelectedLabel(label)}
                        >
                          <span className={styles.labelName}>{label.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )
          ) : null}
          
          {selectedProjectId > 0 && Object.keys(grouped).length === 0 && (
            <div className={styles.noSearchResults}>
              <div className={styles.noResultsIcon}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <h4>No policies found</h4>
              <p>Try adjusting your search terms or browse all policies</p>
              {searchTerm && (
                <button
                  className={styles.clearSearchButton}
                  onClick={() => setSearchTerm('')}
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.policyDetailPanel}>
        {selectedLabel ? (
          <div className={styles.policyDetail}>
            <div className={styles.policyHeader}>
              <div className={styles.policyTitle}>
                <h2>{selectedLabel.name}</h2>
              </div>
              <div className={styles.policyMeta}>
                <div className={styles.metaItem}>
                  <i className="fas fa-clock"></i>
                  <span>Last updated: {new Date(selectedLabel.updated_at).toLocaleDateString()}</span>
                </div>
                {selectedLabel.label_category && (
                  <div className={styles.metaItem}>
                    <i className="fas fa-tag"></i>
                    <span>Category: {selectedLabel.label_category.name}</span>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.policyContent}>
              <div className={styles.contentSection}>
                <h4>Definition</h4>
                <div
                  className={styles.definitionContent}
                  dangerouslySetInnerHTML={{
                    __html: selectedLabel.definition || '<em>No definition provided</em>'
                  }}
                />
              </div>

              <div className={styles.policyActions}>
                {isAdminOrQA && (
                  <Link
                    to={`/policy/update/${selectedLabel.id}`}
                    className={styles.editButton}
                  >
                    <i className="fas fa-edit"></i>
                    Edit Policy
                  </Link>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.noSelectionState}>
            <div className={styles.noSelectionIcon}>
              <i className="fas fa-hand-pointer"></i>
            </div>
            <h3>Select a Policy</h3>
            <p>Choose a policy from the sidebar to view its details</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Policy;
